import type {
  AnnualRevenue,
  ProductDescription,
} from "@editor/utils/onboarding";

import React, { useState } from "react";

import Input from "@common/designSystem/Input";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { onboardingRouter } from "@editor/trpc/onboarding";
import { formatShopNameToSubdomain } from "@editor/utils/onboarding";

import IconButton from "@replo/design-system/components/button/IconButton";
import SelectableChip from "@replo/design-system/components/chip/SelectableChip";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  CalendarClock,
  Check,
  Download,
  GraduationCap,
  Handshake,
  Info,
  LockOpen,
  Repeat,
  Shirt,
  Store,
} from "lucide-react";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

interface OnboardingShopInfoContentProps {
  shopName: string;
  setShopName: (value: string) => void;
  subdomain: string;
  setSubdomain: (value: string) => void;
  onSubdomainBlur?: (value: string) => void;
  productDescription: ProductDescription | null;
  setProductDescription: (value: ProductDescription) => void;
  annualRevenue: AnnualRevenue | null;
  setAnnualRevenue: (value: AnnualRevenue) => void;
  isBusinessContext?: boolean;
  isShopifyPaymentProcessor?: boolean;
  onFormValidityChange?: (isValid: boolean) => void;
}

const OnboardingShopInfoContent: React.FC<OnboardingShopInfoContentProps> = ({
  shopName,
  setShopName,
  subdomain,
  setSubdomain,
  onSubdomainBlur,
  productDescription,
  setProductDescription,
  annualRevenue,
  setAnnualRevenue,
  isBusinessContext = true,
  isShopifyPaymentProcessor = false,
  onFormValidityChange,
}) => {
  const { project: currentProject } = useCurrentProjectContext();

  const handleShopNameChange = (value: string) => {
    setShopName(value);
    if (!hasExistingSubdomain && !hasManuallyEditedSubdomain) {
      if (value) {
        const formattedSubdomain = formatShopNameToSubdomain(value);
        updateSubdomain(formattedSubdomain);
      } else {
        updateSubdomain("");
      }
    }
  };

  const handleSubdomainChange = (value: string) => {
    setHasManuallyEditedSubdomain(true);
    const sanitizedValue = sanitizeSubdomain(value);
    updateSubdomain(sanitizedValue);
  };

  const sanitizeSubdomain = (input: string): string => {
    return input
      .toLowerCase()
      .replace(/[^\d\sa-z-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+/g, "");
  };

  // NOTE (Berkley) 2025-08-14: We cannot remove trailing hyphens in the initial sanitation because then it will be impossible to add a hyphen
  // Instead, we need to remove them on unfocus if present
  const removeTrailingHyphens = (input: string): string => {
    return sanitizeSubdomain(input).replace(/-+$/g, "");
  };

  const [debouncedSubdomain, setDebouncedSubdomain] = useState(subdomain);
  const [hasManuallyEditedSubdomain, setHasManuallyEditedSubdomain] =
    useState(false);

  const debouncedSetSubdomain = useDebouncedCallback(
    (value: string) => setDebouncedSubdomain(value),
    500,
  );

  // Wrapper function to update both immediate and debounced subdomain
  const updateSubdomain = (value: string) => {
    setSubdomain(value);
    debouncedSetSubdomain(value);
  };

  const hasExistingSubdomain = false;

  const { data: availabilityData, isLoading: isCheckingAvailability } =
    onboardingRouter.checkSubdomainAvailability.useQuery(
      { subdomain: debouncedSubdomain },
      {
        enabled: Boolean(debouncedSubdomain && !hasExistingSubdomain),
        refetchOnWindowFocus: false,
      },
    );

  const isSubdomainValid = (() => {
    if (hasExistingSubdomain) {
      return true;
    }
    if (!debouncedSubdomain || isCheckingAvailability) {
      return false;
    }
    return availabilityData?.available === true;
  })();

  const isFormValid =
    shopName.trim() !== "" &&
    (isShopifyPaymentProcessor ||
      hasExistingSubdomain ||
      subdomain.trim() !== "") &&
    (isShopifyPaymentProcessor || productDescription !== null) &&
    annualRevenue !== null &&
    (isShopifyPaymentProcessor || isSubdomainValid);

  // Note (Noah, 2025-09-26, REPL-19228): Weird to have to do this effect here - we should
  // really just be using a react-hook-form and managing the form state in the parent. Also
  // Note we can't use useOnValueChange here because we want to report it the very first render
  // as well. This is an inversion of state which is very bad and should be fixed
  React.useEffect(() => {
    onFormValidityChange?.(isFormValid);
  }, [isFormValid, onFormValidityChange]);

  return (
    <div className="flex flex-col gap-6">
      <div
        className={twMerge(
          "grid gap-6 items-start",
          isShopifyPaymentProcessor
            ? "grid-cols-1"
            : "grid-cols-1 md:grid-cols-2",
        )}
      >
        <div className="flex flex-col gap-3">
          <label className="text-default text-sm md:text-base font-medium flex items-center h-6">
            <span>
              {isBusinessContext
                ? "What's the name of your business?"
                : "What's the name of your client's business?"}
            </span>
          </label>
          <Input
            type="text"
            value={shopName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleShopNameChange(e.target.value)
            }
            size="base"
            placeholder="Enter your shop's name"
            autoFocus
          />
        </div>

        {!isShopifyPaymentProcessor && (
          <div className="flex flex-col gap-3">
            <label className="text-default text-sm md:text-base font-medium flex items-center justify-between h-6">
              <span className="flex items-center gap-2">
                {isBusinessContext
                  ? "Confirm your subdomain"
                  : "Choose a name for your storefront URL"}
                {!hasExistingSubdomain &&
                  debouncedSubdomain &&
                  !isCheckingAvailability &&
                  (() => {
                    if (availabilityData?.available === true) {
                      return <Check size={16} className="text-success" />;
                    }
                    if (availabilityData?.available === false) {
                      return (
                        <span className="text-danger text-sm">
                          This subdomain is already taken
                        </span>
                      );
                    }
                    return null;
                  })()}
              </span>
              <Tooltip
                content={
                  <div className="flex flex-col gap-1 max-w-[200px] py-1 rounded-md">
                    <div className="font-semibold">Domains in Replo</div>
                    <div>
                      Heads up: Once you choose your shop's default Replo
                      subdomain, it can't be changed. You can still connect your
                      own custom domain anytime.
                    </div>
                  </div>
                }
                triggerAsChild
                side="right"
              >
                <IconButton
                  variant="tertiary"
                  icon={<Info size={14} />}
                  size="sm"
                  layoutClassName="w-4 h-4"
                  UNSAFE_className="text-muted hover:text-default"
                />
              </Tooltip>
            </label>
            <div className="flex flex-col gap-1">
              <div
                className={twMerge(
                  "bg-sheet justify-content-around text-default border-0.5 relative flex items-center rounded-md px-[1px] transition-all duration-75 focus-within:border-transparent h-8 max-sm:h-10 border-border-component",
                  hasExistingSubdomain ? "opacity-75" : "",
                )}
              >
                <div className="justify-content-around relative flex h-full flex-1 grow items-center rounded-md transition-all duration-75 focus-within:ring-primary focus-within:ring-1 focus-within:ring-inset gap-2 px-2">
                  <input
                    type="text"
                    value={
                      hasExistingSubdomain
                        ? currentProject?.shopDomainPrefix ?? subdomain
                        : subdomain
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!hasExistingSubdomain) {
                        handleSubdomainChange(e.target.value);
                      }
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      if (!hasExistingSubdomain) {
                        const finalValue = removeTrailingHyphens(
                          e.target.value,
                        );
                        updateSubdomain(finalValue);
                        onSubdomainBlur?.(finalValue);
                      }
                    }}
                    placeholder={
                      hasExistingSubdomain ? "" : "Enter your subdomain"
                    }
                    disabled={hasExistingSubdomain}
                    className={twMerge(
                      "bg-transparent text-default text-base leading-8 border-0 rounded-sm w-full focus:outline-hidden h-full max-sm:text-lg max-sm:leading-normal placeholder:text-placeholder placeholder:font-normal max-sm:placeholder:text-lg disabled:cursor-not-allowed focus-visible:ring-0 flex-1 min-w-0",
                      hasExistingSubdomain ? "cursor-not-allowed" : "",
                    )}
                  />
                  {isCheckingAvailability && (
                    <div className="text-muted text-xs">Checking...</div>
                  )}
                </div>
                <div className="bg-surface px-3 text-muted text-sm flex items-center border-l border-border h-full rounded-r-md">
                  .reploshops.com
                </div>
              </div>
              {hasExistingSubdomain && (
                <div className="text-muted text-sm">
                  This subdomain is already set and cannot be changed
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!isShopifyPaymentProcessor && (
        <div className="flex flex-col gap-3">
          <label className="text-default text-sm md:text-base font-medium flex items-center justify-between">
            <span>
              {isBusinessContext
                ? "How do you make money?"
                : "How does your client make money?"}
            </span>
            <Tooltip
              content={
                <div className="flex flex-col gap-1 max-w-[200px] py-1 rounded-md">
                  <div className="font-semibold">
                    Why does Replo need this info?
                  </div>
                  <div>
                    We use your selection to customize your starting
                    templates—so you can hit the ground running.
                  </div>
                </div>
              }
              triggerAsChild
              side="right"
            >
              <IconButton
                variant="tertiary"
                icon={<Info size={14} />}
                size="sm"
                layoutClassName="w-4 h-4"
                UNSAFE_className="text-muted hover:text-default"
              />
            </Tooltip>
          </label>
          <div className="flex flex-col gap-4">
            {/* Top row - 4 cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "shopify"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("shopify")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "shopify"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "shopify" ? (
                    <img
                      src="/images/onboarding/shopify-icon-selected.svg"
                      alt="Shopify"
                      className="w-8 h-8"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-sheet border border-border rounded-md flex items-center justify-center overflow-hidden">
                      <img
                        src="/images/onboarding/shopify-icon.svg"
                        alt="Shopify"
                        className="w-8 h-8 scale-180"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "shopify" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "shopify"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Shopify
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "shopify" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "shopify"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Use our Shopify integration to build with an existing
                      Shopify storefront and pay with Shopify checkout.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "ecommerce"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("ecommerce")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "ecommerce"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "ecommerce" ? (
                    <img
                      src="/images/onboarding/physical.png"
                      alt="Physical"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<Shirt size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "ecommerce"
                          ? ""
                          : "text-default",
                      )}
                      style={
                        productDescription === "ecommerce"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Ecommerce
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "ecommerce" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "ecommerce"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell physical products with Replo. Upload products, manage
                      orders, and deliver anywhere.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "software"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("software")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "software"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "software" ? (
                    <img
                      src="/images/onboarding/software.png"
                      alt="Software"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<Repeat size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "software" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "software"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Software
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "software" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "software"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell software or apps that are one-time or subscriptions.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "digital"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("digital")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "digital"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "digital" ? (
                    <img
                      src="/images/onboarding/digital.png"
                      alt="Digital"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<Download size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "digital" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "digital"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Digital
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "digital" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "digital"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell assets like designs, PDFs, or files, delivered by
                      download.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Bottom row - 5 cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "membership"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("membership")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "membership"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "membership" ? (
                    <img
                      src="/images/onboarding/membership.png"
                      alt="Membership"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<LockOpen size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "membership"
                          ? ""
                          : "text-default",
                      )}
                      style={
                        productDescription === "membership"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Membership
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "membership" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "membership"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell access to viewing gated online content.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "courses"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("courses")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "courses"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "courses" ? (
                    <img
                      src="/images/onboarding/courses.png"
                      alt="Courses"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<GraduationCap size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "courses" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "courses"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Courses
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "courses" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "courses"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell structured lessons delivered via platform page.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "professional"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("professional")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "professional"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "professional" ? (
                    <img
                      src="/images/onboarding/professional-services.png"
                      alt="Professional Services"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<Handshake size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "professional"
                          ? ""
                          : "text-default",
                      )}
                      style={
                        productDescription === "professional"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Professional Services
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "professional"
                          ? ""
                          : "text-muted",
                      )}
                      style={
                        productDescription === "professional"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Consulting, calendars, coaching, freelance work, or expert
                      help.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "events"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("events")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "events"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "events" ? (
                    <img
                      src="/images/onboarding/events.png"
                      alt="Events & Experiences"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<CalendarClock size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "events" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "events"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Events & Experiences
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "events" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "events"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Live webinars, workshops, classes, or in-person
                      experiences.
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={twMerge(
                  "border-border-component border-0.5 rounded-xl shadow-button-selectable p-4 relative cursor-pointer transition-colors duration-300 hover:bg-sheet",
                  productDescription === "retail"
                    ? "border-transparent"
                    : "border-border",
                )}
                onClick={() => setProductDescription("retail")}
              >
                <img
                  src="/images/onboarding/product-card-bg.png"
                  alt="Replo Background"
                  className={twMerge(
                    "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                    productDescription === "retail"
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex flex-col gap-2 relative z-10">
                  {productDescription === "retail" ? (
                    <img
                      src="/images/onboarding/retail.png"
                      alt="Local Retail"
                      className="w-8 h-8"
                    />
                  ) : (
                    <IconButton
                      variant="tertiary"
                      icon={<Store size={16} />}
                      size="base"
                      layoutClassName="w-8 h-8"
                      UNSAFE_className="bg-sheet border border-border rounded-md"
                    />
                  )}
                  <div className="flex flex-col gap-1">
                    <div
                      className={twMerge(
                        "text-base font-semibold transition-colors duration-300",
                        productDescription === "retail" ? "" : "text-default",
                      )}
                      style={
                        productDescription === "retail"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Local Retail
                    </div>
                    <div
                      className={twMerge(
                        "text-sm transition-colors duration-300",
                        productDescription === "retail" ? "" : "text-muted",
                      )}
                      style={
                        productDescription === "retail"
                          ? { color: "#F5FAD9" }
                          : {}
                      }
                    >
                      Sell locally at cafes, boutiques, food & beverage vendors.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <label className="text-default text-sm md:text-base font-medium">
          {isBusinessContext
            ? "What's your annual revenue?"
            : "What's your client's annual revenue?"}
        </label>
        <div className="flex flex-wrap gap-4">
          <SelectableChip
            size="base"
            active={annualRevenue === "0-500k"}
            onChange={() => setAnnualRevenue("0-500k")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "0-500k" ? { color: "#F5FAD9" } : {}}
          >
            $0 - $500K
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "500k-2m"}
            onChange={() => setAnnualRevenue("500k-2m")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "500k-2m" ? { color: "#F5FAD9" } : {}}
          >
            $500K - $2M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "2m-5m"}
            onChange={() => setAnnualRevenue("2m-5m")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "2m-5m" ? { color: "#F5FAD9" } : {}}
          >
            $2M - $5M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "5m-10m"}
            onChange={() => setAnnualRevenue("5m-10m")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "5m-10m" ? { color: "#F5FAD9" } : {}}
          >
            $5M - $10M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "10m-50m"}
            onChange={() => setAnnualRevenue("10m-50m")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "10m-50m" ? { color: "#F5FAD9" } : {}}
          >
            $10M - $50M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "50m+"}
            onChange={() => setAnnualRevenue("50m+")}
            UNSAFE_activeClassName="bg-black border-black hover:bg-black"
            showHoverStateWhenActive={false}
            style={annualRevenue === "50m+" ? { color: "#F5FAD9" } : {}}
          >
            $50M+
          </SelectableChip>
        </div>
      </div>
    </div>
  );
};

export default OnboardingShopInfoContent;
export type { ProductDescription, AnnualRevenue };
